/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit';

import * as API from '@ibm-watson/shared/modules/actions/api/data-types/index';
import { STATUS } from '@ibm-watson/shared/modules/ia/store/helper';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ApiStatus } from '../../../../../ts';
import { DataType } from '../../../../../ts/action';
import { CreateAsyncThunk, DataTypesSlice } from '../../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import {
  DATA_TYPE_RESPONSE_TYPES,
  isPatternsEntity
} from '../../../util/actionUtil';
import { getById as getEntityById } from '../../entities/selectors';
import { entityAdapter } from '../adapter';

export const listDataTypes = createAsyncThunk<
  DataType[],
  void,
  CreateAsyncThunk
>(
  'data-types/list',
  async (_, { dispatch, getState }) => {
    try {
      const state = getState();
      const skillId = getActionSkillId(state);
      // Use revised API for Access Control improvements if enabled & isNewIA
      // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listCustomDataTypes
      const assistantId = getAssistantId(state);
      const isNewIA = selectIsNewIa(state);
      const useNewIAUpdatedAccess =
        isNewIA && selectIsAccessUpdatesEnabled(state);
      if (skillId) {
        const response = await API.listCustomDataTypes(
          skillId,
          useNewIAUpdatedAccess,
          assistantId
        );
        let pagination = response.pagination;
        const result = [...response.data_types];
        while (pagination?.next_url) {
          const moreResponse = await API.listNextPageCustomDataTypes(
            pagination.next_url
          );
          result.push(...moreResponse.data_types);
          pagination = moreResponse.pagination;
        }

        return result.map(dataType => {
          const newType = { ...dataType };
          const entity = getEntityById(state, dataType.entity);
          if (
            dataType?.response?.response_type ===
            DATA_TYPE_RESPONSE_TYPES.option.id
          ) {
            newType.response.response_type = DATA_TYPE_RESPONSE_TYPES.option.id;
            newType.response_type = DATA_TYPE_RESPONSE_TYPES.option.id;
            newType.num_values = dataType.response?.options?.length || 0;
          } else if (isPatternsEntity(entity)) {
            newType.response_type = DATA_TYPE_RESPONSE_TYPES.regex.id;
            newType.num_values = entity.values.length || 0;
          }
          newType.id = dataType.data_type; //needed to keep Carbon Datatable happy!
          return newType;
        });
      }
      return [];
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { dataTypes } = getState();
      return dataTypes?.listStatus !== STATUS.LOADING;
    }
  }
);

const reducer = {
  [listDataTypes.pending.toString()]: (state: DataTypesSlice) => {
    state.listStatus = ApiStatus.LOADING;
    state.listError = null;
  },
  [listDataTypes.fulfilled.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.listStatus = ApiStatus.SUCCEEDED;
    state.listError = null;
    state.listFetched = Date.now();
    entityAdapter.setAll(state, action.payload);
  },
  [listDataTypes.rejected.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.listStatus = ApiStatus.ERROR;
    state.listError = action.error;
  }
};
export default reducer;
